import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { depositService } from "../../../services/cashier/deposit.service";
import {setGeneralProgressHide, setGeneralProgressShow, setGeneralSnackbarState} from "../../common/ui";
import {setUser} from "../../common/user";

export interface WalletIdentity {
  operator: string;
  brand: string;
  currency: string;
}

export interface PlayerHashResponse {
  playerId: string;
  walletIdentities: WalletIdentity[];
  hash?: string;
}

export interface DepositState {
  playerHash: {
    data: PlayerHashResponse | null;
    error: any;
    isFetching: boolean;
  };
  sendDeposit: {
    data: any;
    error: any;
    isFetching: boolean;
  };
}

const initialState: DepositState = {
  playerHash: {
    data: null,
    error: null,
    isFetching: false,
  },
  sendDeposit: {
    data: null,
    error: null,
    isFetching: false,
  },
};

export const getPlayerHashData: any = createAsyncThunk(
  'cashier/deposit/getPlayerHash',
  async (payload: any, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await depositService.getPlayerByHash(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {

        if (response.status === 404) {
          dispatch(
            setGeneralSnackbarState({
              open: true,
              type: 'warning',
              message: 'error',
              messageKey: 'common.messages.notFound',
            })
          );
          return rejectWithValue({
            error: 'common.messages.notFound'
          })
        } else {
          dispatch(
            setGeneralSnackbarState({
              open: true,
              type: 'warning',
              message: 'error',
              messageKey: 'common.messages.error',
            })
          )
          return rejectWithValue(data)
        }

        dispatch(setGeneralProgressHide());

      }

      dispatch(setGeneralProgressHide());

      return {
        ...payload,
        ...data,
      };
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const sendDepositData: any = createAsyncThunk(
  'cashier/deposit/sendDeposit',
  async (payload: any, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await depositService.sendDeposit(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        dispatch(
          setGeneralSnackbarState({
            open: true,
            type: 'warning',
            message: 'error',
            messageKey: 'common.messages.error',
          })
        )

        if (response.status === 400) {
          dispatch(
            setGeneralSnackbarState({
              open: true,
              type: 'warning',
              message: 'error',
              messageKey: 'common.messages.error',
            })
          );
          return rejectWithValue({
            error: 'common.messages.notFound'
          })
        } else {
          return rejectWithValue(data)
        }
      }

      if (response.ok && response.status === 201) {
        dispatch(
          setGeneralSnackbarState({
            open: true,
            type: 'success',
            message: 'success',
            messageKey: 'common.messages.successSendDeposit',
          })
        );
      }

      dispatch(setGeneralProgressHide());

      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

const depositSlice = createSlice({
  name: "deposit",
  initialState,
  reducers: {
    clearPlayerHashState: (state) => {
      state.playerHash.data = null;
      state.playerHash.error = null;
      state.playerHash.isFetching = false;
    },
    clearSendDepositState: (state) => {
      state.sendDeposit.data = null;
      state.sendDeposit.error = null;
      state.sendDeposit.isFetching = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getPlayerHashData.pending, (state) => {
        state.playerHash.isFetching = true;
        state.playerHash.error = null;
      })
      .addCase(getPlayerHashData.fulfilled, (state, action) => {
        state.playerHash.isFetching = false;
        state.playerHash.error = null;
        state.playerHash.data = action.payload;
      })
      .addCase(getPlayerHashData.rejected, (state, action) => {
        state.playerHash.isFetching = false;
        state.playerHash.error = action.payload;
      })
      .addCase(sendDepositData.pending, (state) => {
        state.sendDeposit.isFetching = true;
        state.sendDeposit.error = null;
      })
      .addCase(sendDepositData.fulfilled, (state, action) => {
        state.sendDeposit.isFetching = false;
        state.sendDeposit.error = null;
        state.sendDeposit.data = action.payload;
      })
      .addCase(sendDepositData.rejected, (state, action) => {
        state.sendDeposit.isFetching = false;
        state.sendDeposit.error = action.payload;
      });
  },
});

export const { clearPlayerHashState, clearSendDepositState } = depositSlice.actions;
export default depositSlice.reducer;
